import { Fragment, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { AlertCircle, CalendarClock, CalendarDays, Loader2, Send } from 'lucide-react'
import { api, ApiError } from '../../lib/api'
import { toast } from '../../lib/dialogs'
import { currentLocaleTag } from '../../lib/formatting'
import { formatCents } from '../../lib/money'
import { useTableSort } from '../../lib/useTableSort'
import EmitInvoiceModal from './EmitInvoiceModal'
import PendingRescheduleModal from './PendingRescheduleModal'
import type { PendingBillingItem, PendingBillingResponse } from '@jr2os/shared'

function errorMessage(err: unknown): string {
  const body = err instanceof ApiError ? (err.body as { error?: string } | undefined) : undefined
  return body?.error ?? (err instanceof Error ? err.message : 'Error')
}

/**
 * «Pendientes»: lo que YA toca facturar (cuotas de pagos recurrentes que han llegado a su fecha y hitos vencidos), sin
 * ningún borrador por medio.
 *
 * Emitir aquí crea la factura y la emite en un solo gesto, con la fecha que se elija (hoy por defecto). Por debajo son
 * las dos llamadas de siempre (crear desde la cuota o el hito, y `/emit`), así que valen las mismas reglas: NIF del
 * cliente, periodo cerrado, numeración de la serie… Si la emisión falla por una de ellas, la factura se queda en
 * BORRADOR —que es donde se puede arreglar— y el aviso lo dice con un enlace; no se pierde nada ni se duplica.
 */
export default function PendingPanel({
  data,
  loading,
  error,
  canWrite,
  onChanged,
}: {
  data: PendingBillingResponse | null
  loading: boolean
  error: boolean
  canWrite: boolean
  onChanged: () => void
}) {
  const { t } = useTranslation('billing')
  const tag = currentLocaleTag()
  const [target, setTarget] = useState<PendingBillingItem | null>(null)
  const [busy, setBusy] = useState(false)
  const [kept, setKept] = useState<{ invoiceId: string; error: string } | null>(null)
  // Reagendar (29-10-2026): la fila cuya fecha se está moviendo.
  const [rescheduleTarget, setRescheduleTarget] = useState<PendingBillingItem | null>(null)

  // Orden por columna. En la URL con su propio `id` (`sort_pending`): la página ya usa `sort`/`dir` para «Emitidas», y la pestaña
  // en la que estamos también vive en la URL (`view=pending`), que el hook respeta al cambiar el orden.
  const { rows, sort, header } = useTableSort(
    data?.items,
    {
      due: (i) => i.dueDate,
      client: (i) => i.clientName,
      concept: (i) => i.label,
      base: (i) => i.baseCents,
      total: (i) => i.totalCents,
    },
    { id: 'pending' }
  )

  const confirmEmit = async (issueDate: string) => {
    if (!target) return
    setBusy(true)
    setKept(null)
    let invoiceId: string | null = null
    try {
      const created =
        target.kind === 'RECURRING'
          ? await api.post<{ invoiceId: string }>(
              `/recurring-invoices/${target.sourceId}/generate-now`,
              // La cuota que se está viendo: si otra persona ya la emitió, el servidor rechaza (409) en vez de emitir la siguiente.
              { expectedRunDate: target.dueDate.slice(0, 10) }
            )
          : await api.post<{ invoiceId: string }>('/milestones/invoice', { ids: [target.sourceId] })
      invoiceId = created.invoiceId
      await api.post(`/invoices/${invoiceId}/emit`, { issueDate })
      toast.success(t('pendingPanel.emitted'))
    } catch (err) {
      const message = errorMessage(err)
      if (invoiceId) setKept({ invoiceId, error: message })
      else toast.error(`${t('pendingPanel.createFailed')}: ${message}`)
    } finally {
      setBusy(false)
      setTarget(null)
      onChanged()
    }
  }

  const dueLabel = (item: PendingBillingItem) =>
    item.upcoming
      ? t('pendingPanel.inDays', { count: item.daysUntil })
      : item.daysLate > 0
        ? t('pendingPanel.daysLate', { count: item.daysLate })
        : t('pendingPanel.dueToday')

  // Tres grupos, ya ordenados por fecha: lo vencido (y de hoy), el resto del mes en curso y el mes siguiente.
  const monthName = (iso: string) =>
    new Date(iso).toLocaleDateString(tag, { month: 'long', timeZone: 'UTC' })
  // El mes en curso, en la zona de la firma (Madrid), que es la que usa el servidor para decidir qué es «hoy»: con el mes UTC del
  // navegador, entre las 00:00 y las 02:00 del día 1 las cuotas del mes en curso caerían en el grupo del mes siguiente.
  const todayMonthKey = new Intl.DateTimeFormat('sv-SE', { timeZone: 'Europe/Madrid' })
    .format(new Date())
    .slice(0, 7)
  const groupOf = (item: PendingBillingItem): 'overdue' | 'thisMonth' | 'nextMonth' =>
    !item.upcoming
      ? 'overdue'
      : item.dueDate.slice(0, 7) === todayMonthKey
        ? 'thisMonth'
        : 'nextMonth'
  // Las bandas de mes no se mezclan: con un orden elegido, cada columna ordena DENTRO de su grupo (vencido → este mes → el
  // siguiente, como siempre). Sin orden elegido, la lista del servidor tal cual.
  const shown = sort.key
    ? (['overdue', 'thisMonth', 'nextMonth'] as const).flatMap((g) =>
        rows.filter((item) => groupOf(item) === g)
      )
    : rows

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-serif font-semibold t-primary flex items-center gap-2">
          <CalendarClock size={18} className="text-jr2-700 dark:text-jr2-300" />
          {t('pendingPanel.title')}
        </h2>
        <p className="text-xs t-muted mt-0.5 max-w-2xl">{t('pendingPanel.subtitle')}</p>
      </div>

      {kept && (
        <div className="surface p-3 text-sm rounded-lg border text-amber-700 dark:text-amber-300 bg-amber-500/10 border-amber-500/30 flex items-center gap-2 flex-wrap">
          <AlertCircle size={14} />
          <span>{t('pendingPanel.draftKept', { error: kept.error })}</span>
          <Link
            to={`/billing/${kept.invoiceId}`}
            className="font-semibold underline hover:no-underline"
          >
            {t('pendingPanel.openDraft')}
          </Link>
        </div>
      )}

      {loading && !data && (
        <div className="surface p-8 text-center t-muted text-sm">
          <Loader2 size={16} className="inline-block animate-spin mr-2" />
          {t('pendingPanel.loading')}
        </div>
      )}

      {error && (
        <div className="surface p-3 text-sm rounded-lg border text-rose-700 dark:text-rose-300 bg-rose-500/10 border-rose-500/30 flex items-center gap-2">
          <AlertCircle size={14} /> {t('pendingPanel.error')}
        </div>
      )}

      {!error && data && data.items.length === 0 && (
        <div className="surface p-10 text-center">
          <CalendarClock size={28} className="mx-auto t-faint mb-2" />
          <p className="t-muted text-sm">{t('pendingPanel.empty')}</p>
        </div>
      )}

      {!error && data && data.items.length > 0 && (
        <div className="surface overflow-x-auto" data-testid="pending-table">
          <table className="w-full text-sm">
            <thead className="bg-[var(--bg-input-soft)] text-xs uppercase tracking-wider t-muted">
              <tr>
                {header('due', t('pendingPanel.table.due'))}
                {header('client', t('pendingPanel.table.client'))}
                {header('concept', t('pendingPanel.table.concept'))}
                {header('base', t('pendingPanel.table.base'), { align: 'right' })}
                {header('total', t('pendingPanel.table.total'), { align: 'right' })}
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {shown.map((item, idx) => {
                const group = groupOf(item)
                const prevGroup = idx > 0 ? groupOf(shown[idx - 1]!) : null
                return (
                  <Fragment key={item.key}>
                    {group !== prevGroup && (
                      <tr
                        className="bg-slate-500 dark:bg-slate-600"
                        data-testid={`pending-group-${group}`}
                      >
                        <td
                          colSpan={6}
                          className="px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-white"
                        >
                          {group === 'overdue'
                            ? t('pendingPanel.group.overdue')
                            : group === 'thisMonth'
                              ? t('pendingPanel.group.thisMonth', {
                                  month: monthName(item.dueDate),
                                })
                              : t('pendingPanel.group.nextMonth', {
                                  month: monthName(item.dueDate),
                                })}
                        </td>
                      </tr>
                    )}
                    <tr className="group border-t border-base/40 hover:bg-hover">
                      <td className="px-3 py-2 whitespace-nowrap">
                        <div className="font-mono text-xs t-primary">
                          {new Date(item.dueDate).toLocaleDateString(tag, { timeZone: 'UTC' })}
                        </div>
                        <div
                          className={`text-[10px] ${
                            !item.upcoming && item.daysLate > 0
                              ? 'text-amber-700 dark:text-amber-300'
                              : 't-faint'
                          }`}
                        >
                          {dueLabel(item)}
                        </div>
                      </td>
                      <td className="px-3 py-2 t-primary">{item.clientName}</td>
                      <td className="px-3 py-2">
                        <span className="inline-flex items-center gap-1.5 flex-wrap">
                          <span
                            className={`inline-flex px-1.5 py-0.5 rounded text-[10px] border ${
                              item.kind === 'MILESTONE'
                                ? 'bg-blue-500/15 text-blue-700 dark:text-blue-300 border-blue-500/30'
                                : 'bg-violet-500/15 text-violet-700 dark:text-violet-300 border-violet-500/30'
                            }`}
                          >
                            {item.kind === 'MILESTONE'
                              ? t('pendingPanel.kindMilestone')
                              : t('pendingPanel.kindRecurring')}
                          </span>
                          <span className="t-primary">{item.label}</span>
                          {item.period && <span className="t-faint">· {item.period}</span>}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right font-mono t-secondary">
                        {formatCents(item.baseCents)}
                      </td>
                      <td className="px-3 py-2 text-right font-mono t-primary">
                        {formatCents(item.totalCents)}
                      </td>
                      {/* Los dos botones en fila y sin partir (con la columna estrecha se apilaban). Con la tabla
                          desplazada en horizontal (pantalla pequeña) la celda se queda pegada a la derecha, para que
                          «Emitir» y «Reagendar» no se pierdan fuera de la vista. El fondo es opaco (si no se verían
                          las columnas pasando por debajo) y en el hover repite el tinte de la fila. */}
                      <td className="px-3 py-2 text-right whitespace-nowrap sticky right-0 bg-[var(--bg-surface)] group-hover:[background-image:linear-gradient(var(--bg-hover),var(--bg-hover))] max-lg:shadow-[-8px_0_8px_-8px_rgba(0,0,0,0.35)]">
                        <div className="flex items-center justify-end gap-1.5">
                          <button
                            type="button"
                            onClick={() => setRescheduleTarget(item)}
                            disabled={
                              !canWrite || busy || (item.kind === 'RECURRING' && !item.canEmit)
                            }
                            title={
                              !canWrite
                                ? t('pendingPanel.reschedule.readOnly')
                                : item.kind === 'RECURRING' && !item.canEmit
                                  ? t('pendingPanel.reschedule.waiting')
                                  : t('pendingPanel.reschedule.button')
                            }
                            aria-label={t('pendingPanel.reschedule.button')}
                            className="w-7 h-7 shrink-0 rounded-lg border border-base t-secondary hover:bg-hover disabled:opacity-40 disabled:hover:bg-transparent grid place-items-center"
                            data-testid="pending-reschedule"
                          >
                            <CalendarDays size={13} />
                          </button>
                          <button
                            type="button"
                            onClick={() => setTarget(item)}
                            disabled={!canWrite || !item.canEmit || busy}
                            title={
                              !canWrite
                                ? t('pendingPanel.readOnly')
                                : !item.canEmit
                                  ? t('pendingPanel.waiting')
                                  : undefined
                            }
                            className="px-3 py-1 shrink-0 rounded-lg text-xs font-semibold text-white bg-jr2-600 hover:bg-jr2-700 disabled:opacity-40 disabled:hover:bg-jr2-600 inline-flex items-center gap-1.5"
                          >
                            <Send size={12} /> {t('pendingPanel.emit')}
                          </button>
                        </div>
                      </td>
                    </tr>
                  </Fragment>
                )
              })}
            </tbody>
            <tfoot>
              {data.count > 0 && (
                <tr className="border-t border-base bg-[var(--bg-input-soft)] font-semibold">
                  <td className="px-3 py-2 t-primary" colSpan={3}>
                    {t('pendingPanel.totalLabel')}
                  </td>
                  <td className="px-3 py-2 text-right font-mono t-secondary">
                    {formatCents(data.totalBaseCents)}
                  </td>
                  <td className="px-3 py-2 text-right font-mono t-primary">
                    {formatCents(data.totalCents)}
                  </td>
                  <td className="px-3 py-2"></td>
                </tr>
              )}
              {data.upcomingCount > 0 && (
                <tr
                  className="bg-[var(--bg-input-soft)] text-xs"
                  data-testid="pending-upcoming-total"
                >
                  <td className="px-3 py-2 t-muted" colSpan={3}>
                    {t('pendingPanel.totalUpcomingLabel')}
                  </td>
                  <td className="px-3 py-2 text-right font-mono t-muted">
                    {formatCents(data.upcomingBaseCents)}
                  </td>
                  <td className="px-3 py-2 text-right font-mono t-secondary">
                    {formatCents(data.upcomingTotalCents)}
                  </td>
                  <td className="px-3 py-2"></td>
                </tr>
              )}
            </tfoot>
          </table>
        </div>
      )}

      <PendingRescheduleModal
        item={rescheduleTarget}
        onClose={() => setRescheduleTarget(null)}
        onDone={() => {
          setRescheduleTarget(null)
          onChanged()
        }}
      />

      <EmitInvoiceModal
        open={target !== null}
        onClose={() => (busy ? undefined : setTarget(null))}
        onConfirm={(issueDate) => void confirmEmit(issueDate)}
        busy={busy}
        summary={
          target
            ? t('pendingPanel.emitSummary', {
                client: target.clientName,
                label: target.period ? `${target.label} · ${target.period}` : target.label,
                amount: formatCents(target.totalCents),
              })
            : undefined
        }
      />
    </div>
  )
}
